import type { MapPing } from "~/shared/components/tracking-map/tracking-map.tsx";
import { isStopActivityStatus } from "~/shared/utils/tracking-status.ts";

type TrackingUpdate = {
	readonly sortDate: string;
	readonly status: string;
	readonly location: string;
	readonly latitude: number | null;
	readonly longitude: number | null;
};

type TrackingStop = {
	readonly sortOrder: number | null;
	readonly stopType: string;
	readonly location: string;
	readonly locationWithZip?: string;
	readonly latitude: number | null;
	readonly longitude: number | null;
	readonly stopDate: string | null;
};

export function buildTrackingPings(
	updates: readonly TrackingUpdate[],
): MapPing[] {
	return updates
		.filter(
			(u): u is typeof u & { latitude: number; longitude: number } =>
				u.latitude != null && u.longitude != null,
		)
		.filter((u) => !isStopActivityStatus(u.status))
		.map((u, i) => ({
			id: `tracking-${String(i)}`,
			latitude: u.latitude,
			longitude: u.longitude,
			type: "tracking" as const,
			label: `${u.location} \u2014 ${u.status}`,
			timestamp: u.sortDate,
		}));
}

export function buildStopPings(stops: readonly TrackingStop[]): MapPing[] {
	const sorted = [...stops]
		.filter(
			(s): s is typeof s & { latitude: number; longitude: number } =>
				s.latitude != null && s.longitude != null,
		)
		.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

	let pickupCount = 0;
	let deliveryCount = 0;

	return sorted.map((s) => {
		const isPickup = s.stopType.toLowerCase() === "pickup";
		const prefix = isPickup ? "P" : "D";
		const index = isPickup ? ++pickupCount : ++deliveryCount;
		const markerLabel = `${prefix}${String(index)}`;

		return {
			id: `stop-${String(s.sortOrder ?? index)}`,
			latitude: s.latitude,
			longitude: s.longitude,
			type: isPickup ? ("pickup" as const) : ("delivery" as const),
			markerLabel,
			label: `${markerLabel} \u2014 ${s.location}`,
		};
	});
}

export function findTerminalStops(stops: readonly TrackingStop[]) {
	const sorted = [...stops].sort(
		(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
	);

	const firstPickup = sorted.find((s) => s.stopType.toLowerCase() === "pickup");
	const finalDrop = [...sorted]
		.toReversed()
		.find((s) => s.stopType.toLowerCase() === "drop");

	return { firstPickup, finalDrop };
}

export function findNextStop(stops: readonly TrackingStop[]): string | null {
	const sorted = [...stops].sort(
		(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
	);

	const now = new Date();
	const nextStop = sorted.find((s) => {
		if (!s.stopDate) return true;
		return new Date(s.stopDate) > now;
	});

	return nextStop?.location ?? null;
}
